import { useContext, useEffect, useState } from 'react';
import { Col, Form } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import * as yup from 'yup';
import { CampoFormik } from '../../../components/CompUtils';
import es from '../../../yup-es';
import { cvTiposSelector } from '../cvSlice';
import { SeccionContext } from '../itemTypes';
import FormikContainer from './FormikContainer';

//
// ─── SCHEMA INSTITUCIONALES ────────────────────────────────────────────────
//

yup.setLocale(es);

const institucionalesSchema = yup
  .object({
    idAntecedenteInstitucional: yup.number(),
    idNivelEducativoCv: yup.number().required(),
    idCargoCv: yup.number().required(),
    otroPuestoDocente: yup.string(),
    asignatura: yup.string().required(),
    institucion: yup.string().required(),
    idPais: yup.number().required(),
    descripcionTarea: yup.string(),
    cargaHorariaSemanalDeLaMateria: yup.number(),
    frecuenciaDeLaMateria: yup.string().required(),
  })
  .shape(
    {
      actualidad: yup.boolean(),
      f_desde: yup
        .date()
        .required('La fecha de ingreso es requerida')
        .test('valid-date', 'Fecha de ingreso inválida', (value) => {
          if (value) {
            const year = value.getFullYear().toString();
            const month = (value.getMonth() + 1).toString().padStart(2, '0');
            const day = value.getDate().toString().padStart(2, '0');
            return (
              year >= '1000' &&
              year <= '9999' &&
              month >= '01' &&
              month <= '12' &&
              day >= '01' &&
              day <= '31'
            );
          }
          return true;
        }),
      f_hasta: yup
        .date()
        .nullable()
        .transform((curr, orig) => (orig === '' ? null : curr))
        .when('actualidad', {
          is: false,
          then: yup.date().required('La fecha hasta es requerida'),
        })
        .when(
          'f_desde',
          (ingreso, schema) =>
            ingreso &&
            yup
              .date()
              .test('valid-date', 'Fecha hasta inválida', (value) => {
                if (value) {
                  const year = value.getFullYear().toString();
                  const month = (value.getMonth() + 1).toString().padStart(2, '0');
                  const day = value.getDate().toString().padStart(2, '0');
                  return (
                    year >= '1000' &&
                    year <= '9999' &&
                    month >= '01' &&
                    month <= '12' &&
                    day >= '01' &&
                    day <= '31'
                  );
                }
                return true;
              })
              .min(yup.ref('f_desde'), 'Rango de fechas incorrecto'),
        ),
    },
    [('f_desde', 'f_hasta', 'actualidad')],
  );
//
// ─── FORMULARIO INSTITUCIONALES ──────────────────────────────────────────────────
//
export const FormInstitucionales = (props) => {
  const extraData = useSelector(cvTiposSelector);
  const niveles = extraData.nivelEducativo;

  const formState = useContext(SeccionContext);

  const [hastaDisabled, setHastaDisabled] = useState(false);
  const [cargos, setCargos] = useState([]);

  const frecuencias = ['Mensual', 'Trimestral', 'Cuatrimestral', 'Semestral', 'Anual', 'Otro'];

  useEffect(() => {
    const initial = formState.initialValues;
    setCargos(niveles[initial.idNivelEducativoCv]?.cargo || []);
    setHastaDisabled(initial.actualidad && !initial.f_hasta);
  }, [formState.initialValues]);

  return (
    <FormikContainer
      schema={institucionalesSchema}
      seccionKey="institucionales"
      seccionTitle="Actividad docente"
      size="lg">
      {(formik) => (
        <>
          <Form.Row>
            <Form.Group as={Col} sm={5}>
              <Form.Label for="nivelEducativo">Nivel educativo (obligatorio)</Form.Label>
              <Form.Control
                aria-required
                id="nivelEducativo"
                as="select"
                name="idNivelEducativoCv"
                value={formik.values.idNivelEducativoCv}
                onBlur={formik.handleBlur}
                isInvalid={formik.touched.idNivelEducativoCv && !!formik.errors.idNivelEducativoCv}
                onChange={(ev) => {
                  setCargos(niveles[parseInt(ev.target.value)].cargo);
                  formik.handleChange(ev);
                }}>
                <option disabled value="">
                  Seleccione
                </option>
                {Object.keys(niveles).map((key) => (
                  <option key={key} value={niveles[key].idNivelEducativoCv}>
                    {niveles[key].descripcion}
                  </option>
                ))}
              </Form.Control>
              <Form.Control.Feedback type="invalid">
                {formik.errors.idNivelEducativoCv}
              </Form.Control.Feedback>
            </Form.Group>
            <Form.Group as={Col} sm={7}>
              <Form.Label for="idCargo">Cargo/Tipo de puesto (obligatorio)</Form.Label>
              <Form.Control
                aria-required
                id="idCargo"
                as="select"
                name="idCargoCv"
                value={formik.values.idCargoCv}
                onBlur={formik.handleBlur}
                isInvalid={formik.touched.idCargoCv && !!formik.errors.idCargoCv}
                onChange={formik.handleChange}>
                <option disabled value="">
                  Seleccione
                </option>
                {cargos.map((cargo, key) => (
                  <option title={cargo.descripcion} key={cargo.idCargoCv} value={cargo.idCargoCv}>
                    {cargo.descripcion}
                  </option>
                ))}
              </Form.Control>
              <Form.Control.Feedback type="invalid">
                {formik.errors.idCargoCv}
              </Form.Control.Feedback>
            </Form.Group>
          </Form.Row>
          <Form.Row>
            <CampoFormik
              col={{ sm: 4 }}
              label="Otro puesto docente"
              name="otroPuestoDocente"
              formik={formik}
            />
            <CampoFormik
              aria-required
              label="Asignatura (obligatorio)"
              name="asignatura"
              formik={formik}
            />
          </Form.Row>
          <Form.Row>
            <CampoFormik
              aria-required
              col={{ sm: 8 }}
              label="Institución académica (obligatorio)"
              name="institucion"
              formik={formik}
            />
          </Form.Row>
          <Form.Row>
            <CampoFormik
              aria-required
              col={{ sm: 8 }}
              type="pais"
              label="País (obligatorio)"
              name="idPais"
              formik={formik}
            />
          </Form.Row>
          <Form.Row>
            <CampoFormik
              type="textarea"
              label="Descripción de la tarea"
              name="descripcionTarea"
              formik={formik}
            />
          </Form.Row>
          <Form.Row>
            <Form.Group as={Col} sm={5}>
              <Form.Label for="frecuencia">Frecuencia de la materia (obligatorio)</Form.Label>
              <Form.Control
                aria-required
                id="frecuencia"
                as="select"
                name="frecuenciaDeLaMateria"
                value={formik.values.frecuenciaDeLaMateria}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                isInvalid={
                  formik.touched.frecuenciaDeLaMateria && !!formik.errors.frecuenciaDeLaMateria
                }>
                <option value="" disabled>
                  Seleccione
                </option>
                {frecuencias.map((fr, idx) => (
                  <option value={fr} key={idx}>
                    {fr}
                  </option>
                ))}
              </Form.Control>
              <Form.Control.Feedback type="invalid">
                {formik.errors.frecuenciaDeLaMateria}
              </Form.Control.Feedback>
            </Form.Group>
            <CampoFormik
              style={{ width: 65 }}
              label="Carga horaria semanal de la materia (en horas)"
              name="cargaHorariaSemanalDeLaMateria"
              formik={formik}
            />
          </Form.Row>
          <Form.Row>
            <CampoFormik
              aria-required
              label="Desde (obligatorio)"
              type="date2"
              name="f_desde"
              maxDate={formik.values.f_hasta}
              col={{ sm: 4 }}
              formik={formik}
            />
            <fieldset style={{ display: 'contents' }} disabled={hastaDisabled}>
              <CampoFormik
                aria-required
                label="Hasta (obligatorio)"
                type="date2"
                name="f_hasta"
                minDate={formik.values.f_desde}
                col={{ sm: 4 }}
                formik={formik}
              />
            </fieldset>
            <Form.Group>
              <Form.Label for="actualidad">&nbsp;</Form.Label>
              <Form.Check
                id="actualidad"
                style={{ transform: 'translateY(25%)' }}
                type="checkbox"
                label="Actualidad"
                name="actualidad"
                checked={formik.values.actualidad}
                onBlur={formik.handleBlur}
                onChange={(ev) => {
                  formik.setFieldValue('actualidad', !formik.values.actualidad);
                  if (ev.target.checked) {
                    formik.setFieldValue('f_hasta', '');
                    setHastaDisabled(true);
                  } else {
                    setHastaDisabled(false);
                  }
                  formik.validateField('f_hasta'); //Para validar required o no segun el caso
                }}
              />
            </Form.Group>
          </Form.Row>
        </>
      )}
    </FormikContainer>
  );
};

export default FormInstitucionales;
